/**
 * OScroll — scroll container with OVERLAY scrollbars.
 *
 * The scrollbar floats over the content: it takes no width (no reserved strip)
 * and does not shift the layout when it appears (no jump). This replaces
 * `scrollbar-gutter: stable` on the shell's main areas, which could only pick
 * one of the two evils — a permanent 15px strip on short pages, or a 15px
 * horizontal jump between pages.
 *
 * Same approach already proven in Forge (`control_plane/forge-app`), theme
 * retuned to the Praesto tokens.
 *
 * Structure: host (`className` — sizing) → viewport → content
 * (`contentClassName` — padding/flex). Layout classes for the inner content go
 * on `contentClassName`, not on the host.
 */
import { forwardRef, type ReactNode } from "react";
import {
  OverlayScrollbarsComponent,
  type OverlayScrollbarsComponentRef,
} from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";

const OPTIONS = {
  scrollbars: {
    theme: "os-theme-praesto",
    // `never` = shown whenever the content actually overflows, absent when it
    // does not. NOT `leave` (which is what Forge uses): that hides the bar
    // until the pointer enters the area, so with the mouse still — or on a
    // first look at the page — there is no scrollbar at all, and no hint that
    // the page continues below.
    autoHide: "never" as const,
    clickScroll: true,
  },
};

interface OScrollProps {
  children: ReactNode;
  /** Host sizing (flex-1, h-full, min-h-0). */
  className?: string;
  /** Inner content classes (padding, flex, gap). */
  contentClassName?: string;
  "aria-label"?: string;
}

export const OScroll = forwardRef<OverlayScrollbarsComponentRef, OScrollProps>(
  ({ children, className, contentClassName, ...rest }, ref) => (
    <OverlayScrollbarsComponent
      ref={ref}
      element="div"
      defer
      options={OPTIONS}
      className={className}
      {...rest}
    >
      <div className={contentClassName}>{children}</div>
    </OverlayScrollbarsComponent>
  ),
);

OScroll.displayName = "OScroll";

export type { OverlayScrollbarsComponentRef };
